import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const RevenueVsProfitUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Revenue vs Profit: What Growing Businesses Get Wrong",
    "description": "Understand the critical difference between revenue and profit. Why revenue growth alone can destroy your business.",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://brightsonaccounting.co.uk/logo.png"
      }
    },
    "datePublished": "2026-04-10",
    "dateModified": "2026-04-10"
  };

  return (
    <div className="article-page">
      <SEO
        title="Revenue vs Profit: What Growing Businesses Get Wrong"
        description="Understand the critical difference between revenue and profit. Why revenue growth alone can destroy your business."
        canonical="/blog/revenue-vs-profit-uk"
        keywords="revenue vs profit, business profit, profit margin UK, business revenue, sustainable growth, Wolverhampton accountant"
        schema={JSON.stringify(articleSchema)}
      />

      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Growing your company</span>
            <h1 className="article-main-title">Revenue vs Profit: What Growing Businesses Get Wrong</h1>
            <p className="article-subtitle">Critical Financial Clarity from Brightson Accounting, Wolverhampton</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>10 April 2026</span>
                <Clock size={16} />
                <span>7 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            <p className="article-lead">
              Revenue is the money coming in. Profit is what you actually keep. The difference can make or break your business.
            </p>

            <p>
              Too many business owners celebrate hitting £100k in revenue — while barely making £10k in profit.
            </p>

            <p>
              At <Link to="/services">Brightson Accounting in Wolverhampton</Link>, we help business owners across the West Midlands understand what really matters: sustainable profit.
            </p>

            <div className="info-box">
              <strong>Quick Summary</strong>
              <ul>
                <li>Revenue = total sales. Profit = what's left after costs</li>
                <li>You can grow revenue while profit shrinks</li>
                <li>Focus on profit margins, not just top-line growth</li>
                <li>Track gross profit and net profit separately</li>
                <li>Cash flow matters more than either metric</li>
              </ul>
            </div>

            <h2>What Is Revenue?</h2>

            <p>
              Revenue (also called turnover or sales) is the total amount of money your business brings in from selling products or services.
            </p>

            <p>
              Example:
            </p>

            <ul>
              <li>You sell 100 widgets at £100 each</li>
              <li>Revenue = £10,000</li>
            </ul>

            <p>
              That's it. No costs deducted. Just top-line sales.
            </p>

            <h2>What Is Profit?</h2>

            <p>
              Profit is what's left after you deduct all costs from revenue.
            </p>

            <h3>Types of Profit:</h3>

            <ul>
              <li><strong>Gross Profit:</strong> Revenue minus direct costs (cost of goods sold)</li>
              <li><strong>Net Profit:</strong> Gross profit minus all operating expenses</li>
            </ul>

            <p>
              Example:
            </p>

            <ul>
              <li>Revenue: £10,000</li>
              <li>Cost of goods: £4,000</li>
              <li>Gross profit: £6,000 (60% margin)</li>
              <li>Operating expenses: £4,500</li>
              <li>Net profit: £1,500 (15% margin)</li>
            </ul>

            <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <p style={{ margin: 0 }}>
                <strong>💡 Want to see how much tax you could save?</strong><br/><br/>
                Most business owners we speak to are overpaying without realising it.<br/><br/>
                <Link to="/corporation-tax-calculator" style={{ color: '#4caf50', fontWeight: 600 }}>
                  👉 Try the Corporation Tax Calculator
                </Link>
              </p>
            </div>

            <h2>Why Revenue Alone Is Misleading</h2>

            <p>
              You can double revenue and still go bankrupt if:
            </p>

            <ul>
              <li>Costs grow faster than sales</li>
              <li>You discount too heavily to win business</li>
              <li>Payment terms create cash flow gaps</li>
              <li>You take on unprofitable work to "hit targets"</li>
            </ul>

            <p>
              From what we see with clients in Wolverhampton, many businesses chase £1m in revenue when £500k at better margins would be more profitable.
            </p>

            <h2>The Profit Margin Trap</h2>

            <p>
              Low margins mean you need massive volume to survive.
            </p>

            <p>
              Example:
            </p>

            <ul>
              <li><strong>Business A:</strong> £100k revenue, 40% margin = £40k profit</li>
              <li><strong>Business B:</strong> £200k revenue, 15% margin = £30k profit</li>
            </ul>

            <p>
              Business A makes more profit with half the revenue — and far less stress.
            </p>

            <p>
              Read: <Link to="/blog/grow-business-profitably-uk">How to Grow Your Business Profitably</Link>
            </p>

            <h2>Gross Profit vs Net Profit</h2>

            <h3>Gross Profit Margin:</h3>

            <p>
              (Revenue - Direct Costs) / Revenue × 100
            </p>

            <p>
              This shows how efficiently you deliver your product/service.
            </p>

            <h3>Net Profit Margin:</h3>

            <p>
              (Net Profit / Revenue) × 100
            </p>

            <p>
              This shows overall business profitability after all costs.
            </p>

            <p>
              Target margins vary by industry, but aim for:
            </p>

            <ul>
              <li>Gross margin: 50%+</li>
              <li>Net margin: 15-25%</li>
            </ul>

            <h2>Cash Flow vs Profit</h2>

            <p>
              Here's where it gets tricky: you can be profitable on paper but run out of cash.
            </p>

            <p>
              Why?
            </p>

            <ul>
              <li>Customers pay 30-60 days late</li>
              <li>You pay suppliers upfront</li>
              <li>You invest in stock before selling it</li>
              <li>Large tax bills come due</li>
            </ul>

            <p>
              Many small businesses we work with in Birmingham are "profitable but broke" due to cash flow timing.
            </p>

            <p>
              Read: <Link to="/blog/cash-flow-management-uk">Cash Flow Management for Growing Businesses</Link>
            </p>

            <h2>How to Improve Profit Without Growing Revenue</h2>

            <h3>1. Increase Prices</h3>

            <p>
              A 10% price increase can double profit if margins are tight.
            </p>

            <h3>2. Reduce Direct Costs</h3>

            <ul>
              <li>Negotiate better supplier terms</li>
              <li>Reduce waste and inefficiency</li>
              <li>Automate production/delivery</li>
            </ul>

            <h3>3. Cut Low-Margin Work</h3>

            <p>
              Drop the bottom 20% of clients/products by profit margin.
            </p>

            <h3>4. Reduce Operating Expenses</h3>

            <ul>
              <li>Cancel unused subscriptions</li>
              <li>Negotiate rent and insurance</li>
              <li>Outsource instead of hiring</li>
            </ul>

            <h3>5. Improve Tax Efficiency</h3>

            <p>
              Every pound saved in tax is a pound of profit retained.
            </p>

            <p>
              Read: <Link to="/blog/tax-planning-growing-business-uk">Tax Planning Strategies for Growing Businesses</Link>
            </p>

            <h2>Common Mistakes</h2>

            <ul>
              <li>Celebrating revenue milestones while ignoring profit</li>
              <li>Competing on price instead of value</li>
              <li>Taking on unprofitable work to "stay busy"</li>
              <li>Not tracking profit by product/service/client</li>
              <li>Growing headcount before improving margins</li>
            </ul>

            <h2>What Investors and Banks Care About</h2>

            <p>
              When raising finance, lenders and investors focus on:
            </p>

            <ul>
              <li><strong>Net profit margin:</strong> Shows efficiency</li>
              <li><strong>EBITDA:</strong> Earnings before interest, tax, depreciation, amortization</li>
              <li><strong>Cash flow:</strong> Can you service debt?</li>
              <li><strong>Profit trends:</strong> Is it growing?</li>
            </ul>

            <p>
              Revenue alone doesn't impress anyone if profit is weak.
            </p>

            <h2>Tracking Profit Correctly</h2>

            <p>
              Use accounting software to track:
            </p>

            <ul>
              <li>Revenue by product/service/client</li>
              <li>Direct costs by product/service</li>
              <li>Gross profit margin by category</li>
              <li>Operating expenses as % of revenue</li>
              <li>Net profit margin trends</li>
            </ul>

            <p>
              Review monthly, not just annually.
            </p>

            <h2>Revenue vs Profit in Wolverhampton & the West Midlands</h2>

            <p>
              At Brightson Accounting, we help local businesses:
            </p>

            <ul>
              <li>Track profit by product, service, and client</li>
              <li>Set pricing strategies that protect margins</li>
              <li>Reduce costs without cutting quality</li>
              <li>Improve cash flow and profitability</li>
            </ul>

            <div className="article-cta-box">
              <h2>Ready to Reduce Your Tax Bill?</h2>
              <p>
                We help businesses across Wolverhampton and the West Midlands reduce tax legally, improve cash flow, and stay compliant.
              </p>
              <Link to="/contact" className="article-cta-button">
              <Mail size={20} />
                Book a Free Consultation
              </Link>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This content is for general guidance only. For tailored advice, contact Brightson Accounting.
              </p>
            </div>

          </div>

          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">Need Help?</h3>
              <p>Speak to our FCCA-qualified accountants about improving profit.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/grow-business-profitably-uk" className="related-link">Grow Profitably</Link>
                <Link to="/blog/cash-flow-management-uk" className="related-link">Cash Flow Management</Link>
                <Link to="/blog/scale-business-uk" className="related-link">Scale Your Business</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default RevenueVsProfitUK;
